<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDurationMinutes } from '../helpers';

import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  assignedAgentCount: { type: Number, default: 0 },
  users: { type: Array, default: () => [] },
  inboxCapacityLimits: { type: Array, default: () => [] },
  exclusionRules: { type: Object, default: () => ({}) },
  // { [inboxId]: { name, icon } } used to label the per-inbox limits.
  inboxLookup: { type: Object, default: () => ({}) },
  isFetchingUsers: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'fetchUsers']);

const { t } = useI18n();

const REDESIGN_KEY = 'ASSIGNMENT_POLICY.REDESIGN';
const MAX_VISIBLE_LIMITS = 3;

const showUsers = ref(false);

const userItems = computed(() =>
  props.users.map(user => ({
    name: user.name,
    id: user.id,
    avatarUrl: user.avatarUrl || user.thumbnail,
  }))
);

const limits = computed(() =>
  (props.inboxCapacityLimits || []).map(limit => ({
    id: limit.id || limit.inboxId,
    name:
      props.inboxLookup[limit.inboxId]?.name ||
      t(`${REDESIGN_KEY}.CAPACITY.UNKNOWN_INBOX`, { id: limit.inboxId }),
    icon: props.inboxLookup[limit.inboxId]?.icon || 'i-hugeicons-inbox',
    max: limit.conversationLimit,
  }))
);

const visibleLimits = computed(() => limits.value.slice(0, MAX_VISIBLE_LIMITS));
const hiddenLimitsCount = computed(() =>
  Math.max(limits.value.length - MAX_VISIBLE_LIMITS, 0)
);

const exclusions = computed(() => {
  const items = [];
  const labels = props.exclusionRules?.excludedLabels || [];
  if (labels.length) {
    items.push({
      key: 'labels',
      icon: 'i-hugeicons-tag-01',
      label: t(
        `${REDESIGN_KEY}.CAPACITY.EXCLUDED_LABELS`,
        { n: labels.length },
        labels.length
      ),
      title: labels.join(', '),
    });
  }
  // The capacity form stores this threshold in minutes.
  const older = formatDurationMinutes(
    props.exclusionRules?.excludeOlderThanHours,
    t
  );
  if (older) {
    items.push({
      key: 'older',
      icon: 'i-hugeicons-clock-01',
      label: t(`${REDESIGN_KEY}.CAPACITY.EXCLUDE_OLDER`, { duration: older }),
    });
  }
  return items;
});

const handleEdit = () => {
  emit('edit', props.id);
};

const handleDelete = () => {
  emit('delete', props.id);
};

const toggleUsers = () => {
  showUsers.value = !showUsers.value;
  if (showUsers.value && !props.users?.length) {
    emit('fetchUsers', props.id);
  }
};
</script>

<template>
  <article
    class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-strong motion-reduce:transition-none"
    :data-test-id="`capacity-policy-card-${id}`"
  >
    <div class="flex min-w-0 items-start justify-between gap-3">
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-slate-11"
          aria-hidden="true"
        >
          <Icon icon="i-hugeicons-dashboard-speed-02" class="size-5" />
        </span>
        <div class="flex min-w-0 flex-col items-start gap-1">
          <button
            type="button"
            class="max-w-full text-left text-sm font-semibold text-n-slate-12 hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded"
            :title="name"
            @click="handleEdit"
          >
            <span class="line-clamp-2 break-words">{{ name }}</span>
          </button>
          <span class="text-xs text-n-slate-11">
            {{
              t(
                `${REDESIGN_KEY}.CAPACITY.LIMITS`,
                { n: limits.length },
                limits.length
              )
            }}
          </span>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Button
          v-tooltip.top="t(`${REDESIGN_KEY}.COMMON.EDIT`)"
          icon="i-hugeicons-pencil-edit-02"
          sm
          slate
          ghost
          :aria-label="t(`${REDESIGN_KEY}.COMMON.EDIT_ARIA`, { name })"
          data-test-id="policy-edit"
          @click="handleEdit"
        />
        <Button
          v-tooltip.top="t(`${REDESIGN_KEY}.COMMON.DELETE`)"
          icon="i-hugeicons-delete-02"
          sm
          slate
          ghost
          class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          :aria-label="t(`${REDESIGN_KEY}.COMMON.DELETE`)"
          data-test-id="policy-delete"
          @click="handleDelete"
        />
      </div>
    </div>

    <p
      class="mb-0 line-clamp-2 break-words text-sm leading-relaxed text-n-slate-11"
    >
      {{ description || t(`${REDESIGN_KEY}.COMMON.NO_DESCRIPTION`) }}
    </p>

    <ul v-if="limits.length" class="mb-0 flex flex-wrap gap-1.5">
      <li
        v-for="limit in visibleLimits"
        :key="limit.id"
        class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12"
        :title="limit.name"
      >
        <Icon :icon="limit.icon" class="size-3.5 shrink-0 text-n-slate-11" />
        <span class="truncate">{{ limit.name }}</span>
        <span
          class="shrink-0 rounded bg-n-alpha-2 px-1.5 font-medium tabular-nums"
        >
          {{ t(`${REDESIGN_KEY}.CAPACITY.LIMIT_MAX`, { n: limit.max }) }}
        </span>
      </li>
      <li
        v-if="hiddenLimitsCount"
        class="inline-flex items-center rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
      >
        {{ `+${hiddenLimitsCount}` }}
      </li>
    </ul>

    <ul v-if="exclusions.length" class="mb-0 flex flex-wrap gap-1.5">
      <li
        v-for="item in exclusions"
        :key="item.key"
        class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
        :title="item.title"
      >
        <Icon :icon="item.icon" class="size-3.5 shrink-0 text-n-slate-11" />
        <span class="truncate">{{ item.label }}</span>
      </li>
    </ul>

    <div class="mt-auto flex flex-col gap-2 border-t border-n-weak pt-3">
      <button
        type="button"
        class="flex w-full items-center justify-between gap-2 rounded-md text-sm text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-default disabled:hover:text-n-slate-11"
        :aria-expanded="showUsers"
        :disabled="!assignedAgentCount"
        data-test-id="policy-users-toggle"
        @click="toggleUsers"
      >
        <span class="flex items-center gap-2">
          <Icon icon="i-hugeicons-user-multiple-02" class="size-4" />
          {{
            t(
              `${REDESIGN_KEY}.CAPACITY.AGENTS`,
              { n: assignedAgentCount },
              assignedAgentCount
            )
          }}
        </span>
        <span v-if="assignedAgentCount" class="flex items-center gap-1 text-xs">
          {{
            t(
              showUsers
                ? `${REDESIGN_KEY}.CAPACITY.HIDE_AGENTS`
                : `${REDESIGN_KEY}.CAPACITY.SHOW_AGENTS`
            )
          }}
          <Icon
            :icon="
              showUsers
                ? 'i-hugeicons-arrow-up-01'
                : 'i-hugeicons-arrow-down-01'
            "
            class="size-3.5"
          />
        </span>
      </button>
      <div v-if="showUsers">
        <div
          v-if="isFetchingUsers && !userItems.length"
          class="flex justify-center py-2 text-n-slate-11"
        >
          <Spinner />
        </div>
        <ul v-else-if="userItems.length" class="mb-0 flex flex-wrap gap-1.5">
          <li
            v-for="user in userItems"
            :key="user.id"
            class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-n-weak py-0.5 ltr:pl-0.5 ltr:pr-2 rtl:pr-0.5 rtl:pl-2 text-xs text-n-slate-12"
            :title="user.name"
          >
            <Avatar
              :name="user.name"
              :src="user.avatarUrl"
              :size="20"
              rounded-full
            />
            <span class="truncate">{{ user.name }}</span>
          </li>
        </ul>
        <p v-else class="mb-0 text-xs text-n-slate-11">
          {{ t(`${REDESIGN_KEY}.CAPACITY.NO_AGENTS`) }}
        </p>
      </div>
    </div>
  </article>
</template>
